import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { API_URL } from "../constants"

const initialState = {
  userCredits: null,
  transactions: [],
  loading: false,
  error: null,
  success: false,
}

// Get user credits
export const getUserCredits = createAsyncThunk(
  "credits/getUserCredits",
  async (userId, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/credits/${userId}`, config)
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Manage user credits
export const manageUserCredits = createAsyncThunk(
  "credits/manageUserCredits",
  async ({ userId, creditData }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(`${API_URL}/api/credits/${userId}`, creditData, config)
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Get credit transactions for a specific type
export const getCreditTransactions = createAsyncThunk(
  "credits/getCreditTransactions",
  async ({ userId, creditType }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }
      creditType = creditType ? creditType : "domestic"; // Default to domestic if not provided
      const { data } = await axios.get(`${API_URL}/api/credits/${userId}/${creditType}`, config)
      return { data, creditType }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

const creditSlice = createSlice({
  name: "credits",
  initialState,
  reducers: {
    resetCreditState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
    clearCreditData: (state) => {
      state.userCredits = null
      state.transactions = []
    },
  },
  extraReducers: (builder) => {
    builder
      // Get user credits
      .addCase(getUserCredits.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getUserCredits.fulfilled, (state, action) => {
        state.loading = false
        state.userCredits = action.payload.user
        // Do NOT overwrite transactions here.
        // The payload often contains unfiltered transactions (all types).
        // Keeping this assignment causes the Domestic tab to show "all" on first load.
        // We now rely on getCreditTransactions to populate state.transactions per type.
        // state.transactions = action.payload.transactions
      })
      .addCase(getUserCredits.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Manage user credits
      .addCase(manageUserCredits.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(manageUserCredits.fulfilled, (state, action) => {
        state.loading = false
        state.success = true

        // Update the user's credit balance
        if (state.userCredits) {
          const { creditType, newBalance } = action.payload
          if (creditType === "domestic") {
            state.userCredits.domesticCredits = newBalance
          } else if (creditType === "international") {
            state.userCredits.internationalCredits = newBalance
          } else if (creditType === "button") {
            state.userCredits.buttonCredits = newBalance
          }
        }

        // Add the new transaction to the list
        state.transactions = [action.payload.transaction, ...state.transactions]
      })
      .addCase(manageUserCredits.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get credit transactions
      .addCase(getCreditTransactions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCreditTransactions.fulfilled, (state, action) => {
        state.loading = false
        state.transactions = action.payload.data
      })
      .addCase(getCreditTransactions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { resetCreditState, clearCreditData } = creditSlice.actions

export default creditSlice.reducer